import React, { useId, useState } from 'react';
import { Icon } from '../icons';

/**
 * Accordion — collapsible sections.
 *
 * Props:
 *   items: Array<{ id, title, content, disabled? }>
 *   allowMultiple: boolean              — default false (single-open)
 *   defaultOpen: id | id[]              — uncontrolled initial open state
 *   open / onOpenChange                 — optional controlled open ids (array)
 */
const Accordion = ({
  items = [],
  allowMultiple = false,
  defaultOpen,
  open,
  onOpenChange,
  className = '',
}) => {
  const baseId = useId();
  const normalize = (val) => (Array.isArray(val) ? val : val != null ? [val] : []);
  const [internal, setInternal] = useState(() => normalize(defaultOpen));
  const isControlled = open !== undefined;
  const openIds = isControlled ? normalize(open) : internal;

  const setOpenIds = (next) => {
    if (!isControlled) setInternal(next);
    onOpenChange?.(next);
  };

  const toggle = (id) => {
    const isOpen = openIds.includes(id);
    let next;
    if (allowMultiple) {
      next = isOpen ? openIds.filter((x) => x !== id) : [...openIds, id];
    } else {
      next = isOpen ? [] : [id];
    }
    setOpenIds(next);
  };

  return (
    <div className={`ps-accordion ${className}`.trim()}>
      {items.map((item) => {
        const isOpen = openIds.includes(item.id);
        const headerId = `${baseId}-${item.id}-h`;
        const panelId = `${baseId}-${item.id}-p`;
        return (
          <div key={item.id} className={`ps-accordion__item${isOpen ? ' is-open' : ''}`}>
            <h3 className="ps-accordion__heading">
              <button
                type="button"
                id={headerId}
                className="ps-accordion__trigger"
                aria-expanded={isOpen}
                aria-controls={panelId}
                disabled={item.disabled}
                onClick={() => toggle(item.id)}
              >
                <span className="ps-accordion__title">{item.title}</span>
                <Icon.ChevronDown size={18} className="ps-accordion__chevron" />
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={headerId}
              className="ps-accordion__panel"
              hidden={!isOpen}
            >
              <div className="ps-accordion__content">{item.content}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
